import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DataGrid from "../../components/DataGrid/index.js";
import Toggle from "../../components/DataGrid/toggle.js";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

export default function LinkExternoList() {
  const navigate = useNavigate();
  const { empresaId } = useParams();
  const gridRef = useRef();

  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalCriar, setModalCriar] = useState(false);
  const [criando, setCriando] = useState(false);

  const [novoLink, setNovoLink] = useState({
    dataExpiracao: "",
  });

  useEffect(() => {
    async function carregarEmpresa() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API}/empresa/${empresaId}`);
        setEmpresa(res.data.data);
      } catch (err) {
        console.error(err);
        AlertComponent("Erro", "Empresa não encontrada", { icon: "error" });
        navigate("/clientes");
      } finally {
        setLoading(false);
      }
    }
    carregarEmpresa();
  }, [empresaId, navigate]);

  const handleCriarLink = async () => {
    setCriando(true);
    try {
      await axios.post(`${process.env.REACT_APP_API}/empresa/${empresaId}/links`, {
        dataExpiracao: novoLink.dataExpiracao || null,
      });

      AlertComponent("Sucesso", "Link criado com sucesso!", { icon: "success" });
      setModalCriar(false);
      setNovoLink({ dataExpiracao: "" });
      gridRef.current?.refresh();
    } catch (err) {
      console.error(err);
      AlertComponent("Erro", err?.response?.data?.message ?? "Erro ao criar link", { icon: "error" });
    } finally {
      setCriando(false);
    }
  };

  const handleToggleStatus = async (id, ativo) => {
    try {
      await axios.put(`${process.env.REACT_APP_API}/empresa/${empresaId}/links/${id}/status`);
      gridRef.current?.refresh();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      AlertComponent("Erro", "Erro ao alterar status do link", { icon: "error" });
    }
  };

  const handleCopiarLink = (codigo) => {
    const url = `${window.location.origin}/enviar-certificado/${codigo}`;
    navigator.clipboard.writeText(url);
    AlertComponent("Copiado!", "Link copiado para a área de transferência", { icon: "success", timer: 1500 });
  };

  const columns = [
    {
      field: "codigo",
      header: "Código",
      sortable: true,
      render: (row) => (
        <span className="font-mono text-sm">{row.codigo}</span>
      ),
    },
    {
      field: "dataCriacao",
      header: "Criado em",
      sortable: true,
      render: (row) => new Date(row.dataCriacao).toLocaleDateString("pt-BR"),
    },
    {
      field: "dataExpiracao",
      header: "Expira em",
      sortable: true,
      render: (row) => row.dataExpiracao 
        ? new Date(row.dataExpiracao).toLocaleDateString("pt-BR")
        : <span className="text-gray-400">Sem expiração</span>,
    },
    {
      key: "ativo",
      header: "Ativo",
      className: "text-center",
      headerClassName: "text-center",
      width: "100px",
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Toggle
            value={row.ativo}
            onChange={() => handleToggleStatus(row.id, row.ativo)}
          />
        </div>
      ),
    },
    {
      key: "copiar",
      header: "",
      width: "60px",
      className: "text-center",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleCopiarLink(row.codigo);
          }}
          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
          title="Copiar Link"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
          </svg>
        </button>
      ),
    },
  ];

  if (loading) return <Loading />;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/clientes")}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 mb-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar para clientes
        </button>
        <h1 className="text-xl font-bold">Links de Envio de Certificado</h1>
        <p className="text-sm text-gray-500">
          Cliente: <span className="font-medium text-gray-700">{empresa?.nome}</span>
        </p>
      </div>

      <DataGrid
        ref={gridRef}
        endpoint={`${process.env.REACT_APP_API}/empresa/${empresaId}/links`}
        columns={columns}
        emptyMessage="Nenhum link criado para este cliente"
        emptyAction={
          <button
            onClick={() => setModalCriar(true)}
            className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            Criar primeiro link
          </button>
        }
        headerAction={
          <button
            onClick={() => setModalCriar(true)}
            className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            Novo Link
          </button>
        }
      />

      {/* Modal Criar Link */}
      {modalCriar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Criar Novo Link</h3>
              <p className="text-sm text-gray-500">
                Gere um link para o cliente enviar o certificado digital
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Expiração (opcional)
                </label>
                <input
                  type="date"
                  value={novoLink.dataExpiracao}
                  onChange={(e) => setNovoLink((prev) => ({ ...prev, dataExpiracao: e.target.value }))}
                  className="w-full p-3 border rounded-md"
                  min={new Date().toISOString().split("T")[0]}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deixe vazio para criar um link sem expiração
                </p>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => {
                  setModalCriar(false);
                  setNovoLink({ dataExpiracao: "" });
                }}
                className="px-4 py-2 rounded-md border hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriarLink}
                disabled={criando}
                className="px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {criando ? "Criando..." : "Criar Link"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}